import { useEffect, useState } from "react";
import "./StatsDashboard.scss";
import newRequest from "../../utils/newRequest";
import { useTranslation } from "react-i18next";
import IndicatorCard from "../indicatorCard/IndicatorCard";
import { useSelector } from "react-redux";
import { formatAmountsToShort } from "../../utils/genericFunctions";
import numeral from "numeral";
import { MdOutlineNumbers } from "react-icons/md";
import PortfolioMap from "../portfolioMap/PortfolioMap";
import { ScaleLoader } from "react-spinners";
import { motion } from "framer-motion";
import { useSocket } from "../../redux/useSocket";

const StatsDashboard = () => {
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(false);
    const { currentUser } = useSelector((state) => state.user);
    const socket = useSocket();
    const [premiumArray, setPremiumArray] = useState([]);
    const [commissionArray, setCommissionArray] = useState([]);
    const [premiumTotal, setPremiumTotal] = useState(0);
    const [commissionTotal, setCommissionTotal] = useState(0);
    const [limit, setLimit] = useState(10);
    const [uwStats, setUwStats] = useState(null);

    useEffect(() => {
        if (!socket) return;

        const handler = async () => {
            console.log("got a signal to refresh...");
            await fetchStats(); // your existing function
        };

        socket.on("refreshStats", handler);

        return () => {
            socket.off("refreshStats", handler);
        };
    }, [socket]);


    const menus = [
        { _id: "carrier", title: "carrier", column: "uwGroupByCarrier" },
        { _id: "insured", title: "insured", column: "uwGroupByInsured" },
        { _id: "product", title: "product", column: "uwGroupByProduct" },
        { _id: "branch", title: "branch", column: "uwGroupByBranch" },
    ];
    const [selectedTopnMenu, setSelectedTopnMenu] = useState(menus[0]);
    const [topnMenus, setTopnMenus] = useState(menus);

    const fetchStats = async () => {
        try {
            setIsLoading(true);
            const res = await newRequest.get("/stats/getStats");
            setUwStats(res.data);
            // console.log(`setUwStats  ${JSON.stringify(res.data, null, 2)}`);
            const premiumGroup = getTopEntries(res.data?.combinedGroups?.[selectedTopnMenu?.column], 'policy_lc_premium', limit) || [];
            const commissionGroup = getTopEntries(res.data?.combinedGroups?.[selectedTopnMenu?.column], 'commission_net_lc', limit) || [];
            // console.log(`${[selectedTopnMenu?.column]}  ${JSON.stringify(res.data, null, 2)}`);

            setPremiumArray(premiumGroup);
            setCommissionArray(commissionGroup);

            const totalPremium = premiumGroup
                .reduce(
                    (total, comm) => total + comm.policy_lc_premium,
                    0
                );
            const totalCommission = commissionGroup
                .reduce(
                    (total, comm) => total + comm.commission_lc_amount,
                    0
                );
            setPremiumTotal(totalPremium);
            setCommissionTotal(totalCommission);
            setIsLoading(false);
        } catch (err) {
            setIsLoading(false);
            console.log(err);
        }
    };

    const getTopEntries = (data, field, topN = 10) => {
        // Sort in descending order
        const topEntries = [...data]
            .sort((a, b) => b[field] - a[field])
            .slice(0, topN);

        // Calculate total of the field across top entries
        const total = topEntries.reduce((sum, item) => sum + (item[field] || 0), 0);

        // Add percentage_of_total to each entry
        return topEntries.map(item => {
            const percentage = total > 0 ? (item[field] / total) * 100 : 0;
            return {
                ...item,
                percentage_of_total: +percentage.toFixed(2), // limit to 2 decimal places
            };
        });
    };

    useEffect(() => {
        fetchStats();
    }, [selectedTopnMenu]);

    return (
        <motion.div
            className="stats-dashboard"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, transition: { duration: 1 } }}
            exit={{ opacity: 0, transition: { duration: 1 } }}
        >
            {isLoading ? (
                <div className="loading-content">
                    <ScaleLoader
                        color="var(--primary)"
                        height={50}
                        radius={8}
                        width={4}
                    />
                </div>
            ) : (
                <div className="stats-dashboard-wrapper">
                    <div className="stat-toolbar">
                        {/* <span className="sub-title">{t('snapshot').toUpperCase()}</span> */}
                        <span className="time-ago">{`${t('refreshed').toLowerCase()} ${uwStats?.time_ago_xx}`}</span>
                    </div>
                    <div className="stat-cards-wrapper">
                        {/* PREMIUMS */}
                        <IndicatorCard
                            cardTitle={t('premium').toUpperCase()}
                            periodTitle={t('ytd').toUpperCase()}
                            currency={uwStats?.currency}
                            amountShort={uwStats?.ytd_net_prem_short}
                            amountPerc={uwStats?.ytd_net_prem_perc}
                            amountActual={uwStats?.ytd_net_prem} />
                        <hr className='separator-v' />
                        {/* COMMISSION */}
                        <IndicatorCard
                            cardTitle={t('commission').toUpperCase()}
                            periodTitle={t('ytd').toUpperCase()}
                            currency={uwStats?.currency}
                            amountShort={uwStats?.ytd_net_comm_short}
                            amountPerc={uwStats?.ytd_net_comm_perc}
                            amountActual={uwStats?.ytd_net_comm} />
                        <hr className='separator-v' />
                        {/* PAYMENTS */}
                        <IndicatorCard
                            cardTitle={t('payments').toUpperCase()}
                            periodTitle={t('ytd').toUpperCase()}
                            currency={uwStats?.currency}
                            amountShort={uwStats?.ytd_payment_lc_short}
                            amountPerc={uwStats?.ytd_payment_perc}
                            amountActual={uwStats?.stat_ytd_payment_lc} />
                        <hr className='separator-v' />
                        {/* RECEIPTS */}
                        <IndicatorCard
                            cardTitle={t('receipts').toUpperCase()}
                            periodTitle={t('ytd').toUpperCase()}
                            currency={uwStats?.currency}
                            amountShort={uwStats?.ytd_receipt_lc_short}
                            amountPerc={uwStats?.ytd_receipt_perc}
                            amountActual={uwStats?.stat_ytd_receipt_lc} />
                    </div>
                    <div className="top-n">
                        <div className="top-n-content-wrapper">
                            <hr className='separator-h' />
                            <div style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "space-between", gap: "5px", padding: "0px 5px" }}>
                                <span className="sub-title">{t("viewTopNby", { variable1: limit.toString(), variable2: t(selectedTopnMenu?.title) }).toUpperCase()}</span>
                                <div className="menus-wrapper">
                                    {topnMenus?.map((row, index) => {
                                        return (
                                            <div key={index}
                                                className={`${selectedTopnMenu?._id === row?._id
                                                    ? "single-menu active"
                                                    : "single-menu"
                                                    }`}
                                                onClick={() => setSelectedTopnMenu(row)}>
                                                <span className="sub-title xrsmall">{t(row?.title).toUpperCase()}</span>
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>
                            <div className="top-n-wrapper">
                                <div className="top-n-premium">
                                    <div className="top-n-title-wrapper">
                                        <div className="top-n-title-left">
                                            <span className="sub-title">{t("topN", { limit }).toUpperCase()}</span>
                                            <hr className='separator-v' style={{ height: "10px", width: "2px" }} />
                                            <span className="sub-title boldsmall">{t("topNby", { variable1: t(selectedTopnMenu?.title), variable2: t('premium') }).toUpperCase()}</span>
                                        </div>
                                        <div className="top-n-title-right">
                                            <span className="sub-title">{formatAmountsToShort(premiumTotal)}</span>
                                        </div>
                                    </div>
                                    {premiumArray?.length > 0 ?
                                        <div>
                                            {premiumArray?.map((row, index) => {
                                                return (
                                                    <div key={index} className="top-n-entity-wrapper">
                                                        <div className="top-n-entity-left">
                                                            {/* <IconComponent className="top-n-icon" /> */}
                                                            <span className="sub-title xrsmall" style={{ width: "20px" }}>{index + 1}.</span>
                                                            <span style={{ display: "flex", flexDirection: "row", width: "100%", alignItems: "center", justifyContent: "space-between", gap: "20px" }}>
                                                                {selectedTopnMenu?._id === "carrier" && <span className="sub-title xrsmall" style={{ flex: "2" }}>{row?.carrier_alias_xx}</span>}
                                                                {selectedTopnMenu?._id === "insured" && <span className="sub-title xrsmall" style={{ flex: "2" }}>{row?.insured_alias_xx}</span>}
                                                                {selectedTopnMenu?._id === "product" && <span className="sub-title xrsmall" style={{ flex: "2" }}>{row?.product_name_xx}</span>}
                                                                {selectedTopnMenu?._id === "branch" && <span className="sub-title xrsmall" style={{ flex: "2" }}>{row?.bizunit_name_xx}</span>}
                                                                <span className="sub-title xrsmall" style={{ flex: "1", textAlign: "right" }}>{formatAmountsToShort(row?.policy_lc_premium)}</span>
                                                            </span>
                                                        </div>
                                                        <div className="top-n-entity-right">
                                                            <div className="percentage-bar-container">
                                                                <div
                                                                    className="percentage-bar-fill"
                                                                    style={{ width: `${parseFloat(row?.percentage_of_total)}%` }}
                                                                />
                                                                <span className="percentage-bar-text">{row?.percentage_of_total}%</span>
                                                            </div>
                                                        </div>
                                                        <div className="tooltip">
                                                            <div className="sub-title">{t('premium')}</div>
                                                            <hr className="separator-h" />
                                                            {selectedTopnMenu?._id === "carrier" && <div className="sub-title xrsmall" style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "5px", flex: "2" }}>{row?.carrier_name_xx}</div>}
                                                            {selectedTopnMenu?._id === "carrier" && <div className="sub-title xrsmall" style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "5px", flex: "2" }}><MdOutlineNumbers className="title-icon" /> {row?.pl_ins_ent_code}</div>}

                                                            {selectedTopnMenu?._id === "insured" && <div className="sub-title xrsmall" style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "5px", flex: "2" }}>{row?.insured_name_xx}</div>}
                                                            {selectedTopnMenu?._id === "insured" && <div className="sub-title xrsmall" style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "5px", flex: "2" }}><MdOutlineNumbers className="title-icon" /> {row?.pl_assr_ent_code}</div>}

                                                            {selectedTopnMenu?._id === "product" && <div className="sub-title xrsmall" style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "5px", flex: "2" }}>{row?.product_name_xx}</div>}
                                                            {selectedTopnMenu?._id === "product" && <div className="sub-title xrsmall" style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "5px", flex: "2" }}>{row?.pl_product_code}</div>}

                                                            {selectedTopnMenu?._id === "branch" && <div className="sub-title xrsmall" style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "5px", flex: "2" }}>{row?.bizunit_name_xx}</div>}
                                                            {selectedTopnMenu?._id === "branch" && <div className="sub-title xrsmall" style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "5px", flex: "2" }}>{row?.pl_biz_unit}</div>}

                                                            <div className="sub-title xrsmall" style={{ display: "flex", marginTop: "10px", flexDirection: "row", alignItems: "center", gap: "5px", flex: "1", alignSelf: "flex-end" }}>{`${currentUser?.currency_code_xx} ${numeral(row?.policy_lc_premium).format("0,0.00")}`}</div>
                                                        </div>
                                                    </div>
                                                )
                                            })}
                                        </div>
                                        :
                                        <div className="no-content-wrapper">
                                            <span className="main-title">no data found</span>
                                        </div>}
                                </div>
                                <div className="top-n-commission">
                                    <div className="top-n-title-wrapper">
                                        <div className="top-n-title-left">
                                            <span className="sub-title">{t("topN", { limit }).toUpperCase()}</span>
                                            <hr className='separator-v' style={{ height: "10px", width: "2px" }} />
                                            <span className="sub-title boldsmall">{t("topNby", { variable1: t(selectedTopnMenu?.title), variable2: t('commission') }).toUpperCase()}</span>
                                        </div>
                                        <div className="top-n-title-right">
                                            <span className="sub-title">{formatAmountsToShort(commissionTotal)}</span>
                                        </div>
                                    </div>
                                    {commissionArray?.length > 0 ?
                                        <div>
                                            {commissionArray?.map((row, index) => {
                                                return (
                                                    <div key={index} className="top-n-entity-wrapper">
                                                        <div className="top-n-entity-left">
                                                            {/* <IconComponent className="top-n-icon" /> */}
                                                            <span className="sub-title xrsmall" style={{ width: "20px" }}>{index + 1}.</span>
                                                            <span style={{ display: "flex", flexDirection: "row", width: "100%", alignItems: "center", justifyContent: "space-between", gap: "20px" }}>
                                                                {selectedTopnMenu?._id === "carrier" && <span className="sub-title xrsmall" style={{ flex: "2" }}>{row?.carrier_alias_xx}</span>}
                                                                {selectedTopnMenu?._id === "insured" && <span className="sub-title xrsmall" style={{ flex: "2" }}>{row?.insured_alias_xx}</span>}
                                                                {selectedTopnMenu?._id === "product" && <span className="sub-title xrsmall" style={{ flex: "2" }}>{row?.product_name_xx}</span>}
                                                                {selectedTopnMenu?._id === "branch" && <span className="sub-title xrsmall" style={{ flex: "2" }}>{row?.bizunit_name_xx}</span>}
                                                                <span className="sub-title xrsmall" style={{ flex: "1", textAlign: "right" }}>{formatAmountsToShort(row?.commission_net_lc)}</span>
                                                            </span>
                                                        </div>
                                                        <div className="top-n-entity-right">
                                                            <div className="percentage-bar-container">
                                                                <div
                                                                    className="percentage-bar-fill"
                                                                    style={{ width: `${parseFloat(row?.percentage_of_total)}%` }}
                                                                />
                                                                <span className="percentage-bar-text">{row?.percentage_of_total}%</span>
                                                            </div>
                                                        </div>
                                                        <div className="tooltip">
                                                            <div className="sub-title">{t('commission')}</div>
                                                            <hr className="separator-h" />
                                                            {selectedTopnMenu?._id === "carrier" && <div className="sub-title xrsmall" style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "5px", flex: "2" }}>{row?.carrier_name_xx}</div>}
                                                            {selectedTopnMenu?._id === "carrier" && <div className="sub-title xrsmall" style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "5px", flex: "2" }}><MdOutlineNumbers className="title-icon" /> {row?.pl_ins_ent_code}</div>}

                                                            {selectedTopnMenu?._id === "insured" && <div className="sub-title xrsmall" style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "5px", flex: "2" }}>{row?.insured_name_xx}</div>}
                                                            {selectedTopnMenu?._id === "insured" && <div className="sub-title xrsmall" style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "5px", flex: "2" }}><MdOutlineNumbers className="title-icon" /> {row?.pl_assr_ent_code}</div>}

                                                            {selectedTopnMenu?._id === "product" && <div className="sub-title xrsmall" style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "5px", flex: "2" }}>{row?.product_name_xx}</div>}
                                                            {selectedTopnMenu?._id === "product" && <div className="sub-title xrsmall" style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "5px", flex: "2" }}>{row?.pl_product_code}</div>}

                                                            {selectedTopnMenu?._id === "branch" && <div className="sub-title xrsmall" style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "5px", flex: "2" }}>{row?.bizunit_name_xx}</div>}
                                                            {selectedTopnMenu?._id === "branch" && <div className="sub-title xrsmall" style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "5px", flex: "2" }}>{row?.pl_biz_unit}</div>}

                                                            <div className="sub-title xrsmall" style={{ display: "flex", marginTop: "10px", flexDirection: "row", alignItems: "center", gap: "5px", flex: "1", alignSelf: "flex-end" }}>{`${currentUser?.currency_code_xx} ${numeral(row?.commission_net_lc).format("0,0.00")}`}</div>
                                                        </div>
                                                    </div>
                                                )
                                            })}
                                        </div>
                                        :
                                        <div className="no-content-wrapper">
                                            <span className="main-title">no data found</span>
                                        </div>}
                                </div>
                            </div>
                        </div>
                        <div className="portfolio">
                            <hr className='separator-h' />
                            <PortfolioMap />
                        </div>
                    </div>
                </div>
            )}
        </motion.div>
    );
};

export default StatsDashboard